import { create } from 'zustand';
import { adminResolvedEmailsApi } from '@api/api-config.ts';
import { AdminResolvedEmailListResponse, AdminResolvedEmailDetailResponse } from '@api/generated/portal-be-admin';

export interface ResolvedEmailFilters {
  search?: string;
  status?: string;
  organizationId?: number;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface AdminResolvedEmailStoreState {
  loading: boolean;
  error: string | null;
  data: AdminResolvedEmailListResponse[] | null;
  pagination: PaginationInfo | null;
  detail: AdminResolvedEmailDetailResponse | null;
  detailLoading: boolean;
  retrying: boolean;
  retryError: string | null;
  lastFilters: ResolvedEmailFilters | null;
  loadResolvedEmails: (filters: ResolvedEmailFilters) => Promise<void>;
  loadDetail: (id: number) => Promise<void>;
  retryEmail: (id: number) => Promise<void>;
  clearDetail: () => void;
}

export const useAdminResolvedEmailStore = create<AdminResolvedEmailStoreState>((set, get) => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  detail: null,
  detailLoading: false,
  retrying: false,
  retryError: null,
  lastFilters: null,

  loadResolvedEmails: async (filters: ResolvedEmailFilters) => {
    try {
      set({ loading: true, error: null, lastFilters: filters });
      const response = await adminResolvedEmailsApi.getAdminResolvedEmails({
        page: filters.page,
        size: filters.size,
        search: filters.search,
        status: filters.status as any,
        organizationId: filters.organizationId,
      });
      const result = response.data;
      set({
        loading: false,
        error: null,
        data: result.items || [],
        pagination: {
          page: result.page,
          size: result.size,
          total: result.total,
          totalPages: result.totalPages,
          hasPrev: result.hasPrev,
          hasNext: result.hasNext,
        },
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba pri nacitani emailu',
      });
    }
  },

  loadDetail: async (id: number) => {
    try {
      set({ detailLoading: true, detail: null });
      const response = await adminResolvedEmailsApi.getAdminResolvedEmailDetail({ id });
      set({ detailLoading: false, detail: response.data });
    } catch (error) {
      set({ detailLoading: false, detail: null });
    }
  },

  retryEmail: async (id: number) => {
    try {
      set({ retrying: true, retryError: null });
      const response = await adminResolvedEmailsApi.retryAdminResolvedEmail({ id });
      set({ retrying: false, detail: response.data });
      // refetch seznamu, aby se zmena stavu promitla do tabulky
      const filters = get().lastFilters;
      if (filters) {
        await get().loadResolvedEmails(filters);
      }
    } catch (error) {
      set({
        retrying: false,
        retryError: error instanceof Error ? error.message : 'Chyba pri znovuodeslani emailu',
      });
    }
  },

  clearDetail: () => set({ detail: null, retryError: null }),
}));
